import { createAsyncThunk, createDraftSafeSelector, createSlice } from "@reduxjs/toolkit";
import { getCurrentContractAddresses } from "src/configs/address";
import { environment } from "src/configs/constance";
import FakeNFTABI from "src/contract-logic/abis/Bridge/FakeCollectionABI.json";
import FakeNFTCollectionContract from "src/contract-logic/logic/Bridge/FakeCollection";
import {
  fakeSpecialCollectionAssetsMapping,
  fakeSpecialCollectionIdList,
} from "src/contract-logic/logic/Bridge/FakeData";
import MainCanonical from "src/contract-logic/logic/Bridge/MainCanonical";
import SideCanonical from "src/contract-logic/logic/Bridge/SideCanonical";
import SideCollection from "src/contract-logic/logic/Bridge/SideCollection";
import sidemultiCall from "src/contract-logic/logic/Bridge/sideMulticall";
import RealNFTCollectionContract from "src/contract-logic/logic/NFTCollectionContract";
import multiCall from "src/utils/multicall";
import { sets, specialCollectionAssetsMapping, specialCollectionIdList } from "src/utils/old-version-knight";

const fetchCollectionByListId = async (collectionIdsFlattened) => {
  const specMapping = environment == "mainnet" ? specialCollectionAssetsMapping : fakeSpecialCollectionAssetsMapping;
  const specIds = environment == "mainnet" ? specialCollectionIdList : fakeSpecialCollectionIdList;
  const NFTCollectionContract = environment == "mainnet" ? RealNFTCollectionContract : FakeNFTCollectionContract;
  let [collectionsExp, collectionRarity] = await Promise.all([
    multiCall(
      FakeNFTABI,
      collectionIdsFlattened.map((collectionId) => ({
        address: NFTCollectionContract.ADDRESS,
        name: "getCollectionExperience",
        params: [collectionId],
      }))
    ),
    multiCall(
      FakeNFTABI,
      collectionIdsFlattened.map((collectionId) => ({
        address: NFTCollectionContract.ADDRESS,
        name: "viewCollectionRarity",
        params: [collectionId],
      }))
    ),
  ]);
  collectionsExp = collectionsExp.flat();
  collectionRarity = collectionRarity.flat();
  let collectionsMetadata;
  collectionsMetadata = collectionRarity.map((el, idx) => ({
    rarity: parseInt(el),
    set: sets[1],
    id: parseInt(collectionIdsFlattened[idx]),
    exp: parseInt(collectionsExp[idx]),
  }));

  let specialCollections = collectionsMetadata
    .filter((item) => item.rarity > 5)
    .filter((item) => specIds.includes(item.id));
  // fetch special collections metadata
  let [tokenURIList] = await Promise.all([
    multiCall(
      FakeNFTABI,
      specialCollections.map((item) => ({
        address: NFTCollectionContract.ADDRESS,
        name: "tokenURI",
        params: [item.id],
      }))
    ),
  ]);
  tokenURIList = tokenURIList.flat();
  specialCollections = specialCollections.map((item, idx) => ({
    ...specMapping[item.id],
    ...item,
    rarity: "limited",
    img: specMapping[item.id].imgBg,
    metadataLink: tokenURIList[idx],
    displayedName: specMapping[item.id].name,
  }));
  return specialCollections;
};
export const fetchUnclaimedonBSC = createAsyncThunk("bridgeSlice/fetchUnclaimedonBSC", async (_, thunkAPI) => {
  let accountAddress = thunkAPI.getState().accountSlice.address;
  accountAddress = accountAddress.toLowerCase().substring(2);

  await SideCanonical.getQueueLength();
  const { MAIN_BRIDGE, MAIN_CANONICAL_TRANSACTION_CHAIN, SIDE_CANONICAL_TRANSACTION_CHAIN } =
    getCurrentContractAddresses();
  const NFTCollectionContract = environment == "mainnet" ? RealNFTCollectionContract : FakeNFTCollectionContract;
  const [balanceOfMainBridge, mainQueueLength, sideQueueLength] = await Promise.all([
    NFTCollectionContract.getBalance(MAIN_BRIDGE),
    MainCanonical.getQueueLength(),
    SideCanonical.getQueueLength(),
  ]);

  const tupleMainBridge = new Array(parseInt(balanceOfMainBridge)).fill(1).map((_, idx) => ({
    address: NFTCollectionContract.ADDRESS,
    name: "tokenOfOwnerByIndex",
    params: [MAIN_BRIDGE, idx],
  }));
  const tupleMainQueue = new Array(parseInt(mainQueueLength)).fill(1).map((_, idx) => ({
    address: MAIN_CANONICAL_TRANSACTION_CHAIN,
    name: "getQueueElement",
    params: [idx],
  }));
  const tupleSideQueue = new Array(parseInt(sideQueueLength)).fill(1).map((_, idx) => ({
    address: SIDE_CANONICAL_TRANSACTION_CHAIN,
    name: "getQueueElement",
    params: [idx],
  }));
  let [listIdMainBridge, listMainQueue, listSideQueue] = await Promise.all([
    multiCall(NFTCollectionContract.ABI, tupleMainBridge),
    multiCall(MainCanonical.ABI, tupleMainQueue),
    sidemultiCall(SideCanonical.ABI, tupleSideQueue),
  ]);
  listIdMainBridge = listIdMainBridge.flat().map((e) => parseInt(e));

  const listIdMainQueue = listMainQueue.flat().map((element) => {
    return parseInt(element.message.substring(586 - 64, 586), 16);
  });
  const listIdSideQueue = listSideQueue.flat().map((el) => {
    return parseInt(el.message.substring(394 - 64, 394), 16);
  });
  const listIdMainCopy = [...listIdMainQueue];
  const listIdSideQueueCopy = [...listIdSideQueue];
  listIdSideQueue.forEach((el) => {
    if (listIdMainCopy.indexOf(el) >= 0 && listIdSideQueue.indexOf(el) >= 0) {
      listIdMainCopy.splice(listIdMainCopy.indexOf(el), 1);
      listIdSideQueueCopy.splice(listIdSideQueueCopy.indexOf(el), 1);
    }
  });
  const listUnclaimed = [];
  const listIdSideQueueReverse = [...listIdSideQueue].reverse();
  const listSideQueueReverse = [...listSideQueue].flat().reverse();
  const listInsert = [];
  listIdSideQueueReverse.forEach((el, index) => {
    if (
      !listIdSideQueueCopy.includes(el) &&
      !listIdMainCopy.includes(el) &&
      listSideQueueReverse[index].message.substring(290, 290 + 40) == accountAddress &&
      !listInsert.includes(el) &&
      listIdMainBridge.includes(el)
    ) {
      if (listUnclaimed.indexOf(el) < 0) listUnclaimed.push(el);
    }
    listInsert.push(el);
  });
  return await fetchCollectionByListId(listUnclaimed);
});

export const fetchUnclaimedNFTonETH = createAsyncThunk("bridgeSlice/fetchUnclaimedNFTonETH", async (_, thunkAPI) => {
  let accountAddress = thunkAPI.getState().accountSlice.address;
  const { MAIN_BRIDGE, SIDE_COLLECTION, MAIN_CANONICAL_TRANSACTION_CHAIN, SIDE_CANONICAL_TRANSACTION_CHAIN } =
    getCurrentContractAddresses();
  accountAddress = accountAddress.toLowerCase().substring(2);
  const NFTCollectionContract = environment == "mainnet" ? RealNFTCollectionContract : FakeNFTCollectionContract;

  const [balanceOfMainBridge, balanceOfSideCollection, mainQueueLength, sideQueueLength] = await Promise.all([
    NFTCollectionContract.getBalance(MAIN_BRIDGE),
    SideCollection.totalSupply(),
    MainCanonical.getQueueLength(),
    SideCanonical.getQueueLength(),
  ]);
  const tupleMainBridge = new Array(parseInt(balanceOfMainBridge)).fill(1).map((_, idx) => ({
    address: NFTCollectionContract.ADDRESS,
    name: "tokenOfOwnerByIndex",
    params: [MAIN_BRIDGE, idx],
  }));
  const tupleSideCollection = new Array(parseInt(balanceOfSideCollection)).fill(1).map((_, idx) => ({
    address: SIDE_COLLECTION,
    name: "tokenByIndex",
    params: [idx],
  }));

  const tupleMainQueue = new Array(parseInt(mainQueueLength)).fill(1).map((_, idx) => ({
    address: MAIN_CANONICAL_TRANSACTION_CHAIN,
    name: "getQueueElement",
    params: [idx],
  }));
  const tupleSideQueue = new Array(parseInt(sideQueueLength)).fill(1).map((_, idx) => ({
    address: SIDE_CANONICAL_TRANSACTION_CHAIN,
    name: "getQueueElement",
    params: [idx],
  }));
  let [listIdMainBridge, listIdSideCollection, listMainQueue, listSideQueue] = await Promise.all([
    multiCall(NFTCollectionContract.ABI, tupleMainBridge),
    sidemultiCall(SideCollection.ABI, tupleSideCollection),
    multiCall(MainCanonical.ABI, tupleMainQueue),
    sidemultiCall(SideCanonical.ABI, tupleSideQueue),
  ]);
  listIdMainBridge = listIdMainBridge.flat().map((e) => parseInt(e));
  listIdSideCollection = listIdSideCollection.flat().map((e) => parseInt(e));

  const listIdMainQueue = listMainQueue.flat().map((element) => {
    return parseInt(element.message.substring(586 - 64, 586), 16);
  });
  const listIdSideQueue = listSideQueue.flat().map((el) => {
    return parseInt(el.message.substring(394 - 64, 394), 16);
  });
  const listUnclaimedIndex = [];
  listIdMainQueue.filter((el, index) => {
    let res = listIdSideQueue.indexOf(el) < 0;
    if (!res) {
      listIdSideQueue.splice(listIdSideQueue.indexOf(el), 1);
    } else listUnclaimedIndex.push(index);
    return res;
  });
  const listUnclaimed = listIdMainBridge.filter((el) => {
    return listIdSideCollection.indexOf(el) < 0;
  });
  const collectionIdsFlattened = [];
  listMainQueue = listMainQueue.flat();

  listUnclaimedIndex.forEach((el) => {
    if (
      listUnclaimed.indexOf(listIdMainQueue[el]) >= 0 &&
      listMainQueue[el].message.substring(34 + 64 * 3, 34 + 64 * 3 + 40) === accountAddress
    ) {
      collectionIdsFlattened.push(listIdMainQueue[el]);
    }
  });
  return await fetchCollectionByListId(collectionIdsFlattened);
});
export const fetchOwnedNFTonETH = createAsyncThunk("bridgeSlice/fetchOwnedNFTonETH", async (_, thunkAPI) => {
  const accountAddress = thunkAPI.getState().accountSlice.address;
  const balance = await SideCollection.getBalance(accountAddress);
  const tuple = new Array(parseInt(balance)).fill(1).map((_, idx) => ({
    address: SideCollection.ADDRESS,
    name: "tokenOfOwnerByIndex",
    params: [accountAddress, idx],
  }));

  const [collectionIds] = await Promise.all([sidemultiCall(SideCollection.ABI, tuple)]);
  const collectionFlat = collectionIds.flat().map((el) => parseInt(el));
  return await fetchCollectionByListId(collectionFlat);
});
export const fetchOwnedNFTonBSC = createAsyncThunk("bridgeSlice/fetchOwnedNFTonBSC", async (_, thunkAPI) => {
  const accountAddress = thunkAPI.getState().accountSlice.address;
  const NFTCollectionContract = environment == "mainnet" ? RealNFTCollectionContract : FakeNFTCollectionContract;
  const collectionLength = await NFTCollectionContract.getBalance(accountAddress);
  const [collectionIds] = await Promise.all([
    multiCall(
      FakeNFTABI,
      new Array(collectionLength).fill(1).map((_, idx) => ({
        address: NFTCollectionContract.ADDRESS,
        name: "tokenOfOwnerByIndex",
        params: [accountAddress, idx],
      }))
    ),
  ]);
  const collectionIdsFlattened = collectionIds.flat();
  return await fetchCollectionByListId(collectionIdsFlattened);
});

const initialState = {
  unclaimedETH: undefined,
  ownedETH: undefined,
  unclaimedBSC: undefined,
  sourceChain: undefined,
  destChain: undefined,
  ownedBSC: undefined,
};

const bridgeSlice = createSlice({
  name: "bridgeSlice",
  initialState,
  reducers: {
    updateBridgeChain(state, action) {
      state.sourceChain = action.payload.srcChain;
      state.destChain = action.payload.destChain;
    },
    resetBridgeChain(state, _) {
      state.sourceChain = undefined;
      state.destChain = undefined;
    },
    removeUnclaimedByIndex(state, action) {
      const idx = action.payload.index;
      if (state.unclaimedETH?.[idx]) {
        state.unclaimedETH = state.unclaimedETH.slice(0, idx).concat(state.unclaimedETH.slice(idx + 1));
      }
    },
    removeOwnedOnETHByIndex(state, action) {
      const idx = action.payload.index;
      if (state.ownedETH?.[idx]) {
        state.ownedETH = state.ownedETH.slice(0, idx).concat(state.ownedETH.slice(idx + 1));
      }
    },
    removeOwnedOnBSCByIndex(state, action) {
      const idx = action.payload.index;
      if (state.ownedBSC?.[idx]) {
        state.ownedBSC = state.ownedBSC.slice(0, idx).concat(state.ownedBSC.slice(idx + 1));
      }
    },
    removeUnclaimedOnBSCByIndex(state, action) {
      const idx = action.payload.index;
      if (state.unclaimedBSC?.[idx]) {
        state.unclaimedBSC = state.unclaimedBSC.slice(0, idx).concat(state.unclaimedBSC.slice(idx + 1));
      }
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(fetchUnclaimedNFTonETH.pending, (state) => {
        state.unclaimedETH = undefined;
      })
      .addCase(fetchUnclaimedNFTonETH.fulfilled, (state, action) => {
        state.unclaimedETH = action.payload;
      })
      .addCase(fetchUnclaimedNFTonETH.rejected, (state, action) => {
        state.unclaimedETH = undefined;
        console.error(action);
      })
      .addCase(fetchOwnedNFTonETH.pending, (state, _) => {
        state.ownedETH = undefined;
      })
      .addCase(fetchOwnedNFTonETH.fulfilled, (state, action) => {
        state.ownedETH = action.payload;
      })
      .addCase(fetchOwnedNFTonETH.rejected, (state, action) => {
        state.ownedETH = undefined;
        console.error(action);
      })
      .addCase(fetchUnclaimedonBSC.pending, (state, _) => {
        state.unclaimedBSC = undefined;
      })
      .addCase(fetchUnclaimedonBSC.fulfilled, (state, action) => {
        state.unclaimedBSC = action.payload;
      })
      .addCase(fetchUnclaimedonBSC.rejected, (state, action) => {
        state.unclaimedBSC = undefined;
        console.error(action);
      })
      .addCase(fetchOwnedNFTonBSC.pending, (state, _) => {
        state.ownedBSC = undefined;
      })
      .addCase(fetchOwnedNFTonBSC.fulfilled, (state, action) => {
        state.ownedBSC = action.payload;
      })
      .addCase(fetchOwnedNFTonBSC.rejected, (state, action) => {
        state.ownedBSC = undefined;
        console.error(action);
      }),
});

export default bridgeSlice.reducer;
export const selectBridgeSlice = (state) => state.bridgeSlice;
export const selectUnclaimedETH = createDraftSafeSelector(
  (state) => selectBridgeSlice(state).unclaimedETH,
  (unclaimedETH) => {
    if (!unclaimedETH) return null;
    return unclaimedETH.map((item, index) => ({
      ...item,
      index,
    }));
  }
);
export const selectUnclaimedBSC = createDraftSafeSelector(
  (state) => selectBridgeSlice(state).unclaimedBSC,
  (unclaimedBSC) => {
    if (!unclaimedBSC) return null;
    return unclaimedBSC.map((item, index) => ({
      ...item,
      index,
    }));
  }
);
export const selectOwnedNFTonETH = createDraftSafeSelector(
  (state) => selectBridgeSlice(state).ownedETH,
  (ownedETH) => {
    if (!ownedETH) return null;
    return ownedETH.map((item, index) => ({
      ...item,
      index,
    }));
  }
);
export const selectOwnedNFTonBSC = createDraftSafeSelector(
  (state) => selectBridgeSlice(state).ownedBSC,
  (ownedBSC) => {
    if (!ownedBSC) return null;
    return ownedBSC.map((item, index) => ({
      ...item,
      index,
    }));
  }
);

export const {
  removeUnclaimedByIndex,
  removeOwnedOnETHByIndex,
  removeOwnedOnBSCByIndex,
  removeUnclaimedOnBSCByIndex,
  updateBridgeChain,
  resetBridgeChain,
} = bridgeSlice.actions;
